import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import $ from "jquery";
import { InCoreModal, InCoreModalAction, InCoreModalLayout } from "./modal.utils";
import { InCoreSwal2, InCoreSwal2Icon } from "./swal.utils";

export const InCoreCalendarWindowType = {
    "Modal": "Modal",
    "NewTab": "NewTab",
    "SameWindow": "SameWindow"
};

/**
 * Automatically initializes FullCalendar instances for elements with the `data-calendar` attribute.
 */
export function initializeCalendars(): void {
    $(() => {
        $("div[data-calendar]").each(function () {
            const obj = $(this);
            const id = obj.attr("id")!;
            const name = obj.data("calendar") as string;
            (window as any)["calendar"] = inCoreCalendar(id, name);
        });
    });
}

/**
 * Creates a FullCalendar instance for a specific element.
 * @param {string} id - The ID of the calendar element.
 * @param {string} name - The name of the calendar.
 * @returns {FullCalendar.Calendar} - The initialized FullCalendar instance.
 */
export function inCoreCalendar(id: string, name: string): Calendar {
    const obj = $(`#${id}`);
    const controller = obj.attr("data-controller") ?? "CalendarEvent";
    const action = obj.attr("data-action") ?? undefined;
    const isByUser = obj.attr("data-byuser") === "true";
    const disabled = obj.attr("data-disabled") === "true";
    const readonly = obj.attr("data-readonly") === "true";
    const createParams = obj.attr("data-createParams") ?? null;
    const editParams = obj.attr("data-editParams") ?? null;
    const windowType = obj.attr('data-windowType') ?? InCoreCalendarWindowType.Modal;

    const calendarParams = { name };

    return inCoreCalendarCustom(
        id,
        isByUser,
        controller,
        action,
        disabled,
        readonly,
        calendarParams,
        createParams,
        editParams,
        windowType
    );
}

/**
 * Creates a custom FullCalendar instance with advanced configuration.
 * @param {string} id - The ID of the calendar element.
 * @param {boolean} isByUser - Whether the calendar is filtered by user.
 * @param {string} controller - The controller name for event handling.
 * @param {string | undefined} action - The action name for event fetching.
 * @param {boolean} disabled - Whether the calendar is disabled.
 * @param {boolean} readonly - Whether the calendar is read-only.
 * @param {any} params - Additional parameters for event fetching.
 * @param {any} createParams - Parameters for creating events.
 * @param {any} editParams - Parameters for editing events.
 * @param [windowType=InCoreCalendarWindowType.Modal] - Parameters for type of window to open
 * @returns {FullCalendar.Calendar} - The initialized FullCalendar instance.
 */
export function inCoreCalendarCustom(
    id: string,
    isByUser: boolean,
    controller: string,
    action: string | undefined,
    disabled: boolean,
    readonly: boolean,
    params: any,
    createParams: any = null,
    editParams: any = null,
    windowType: any = InCoreCalendarWindowType.Modal
): Calendar {
    const calElement = document.getElementById(id)!;
    const method = action || (isByUser ? "GetEventsByUser" : "GetEvents");

    const calendar = new Calendar(calElement, {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        eventSources: [
            {
                url: `/${controller}/${method}`,
                extraParams: () => params,
            },
        ],
        initialView: "dayGridMonth",
        locale: "it",
        firstDay: 1,
        themeSystem: "bootstrap5",
        dayMaxEventRows: 4,
        dayMaxEvents: 4,
        headerToolbar: {
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay listWeek",
        },
        dateClick: (info) => {
            if (!disabled && !readonly) {
                const base = controller + "/" + InCoreModalAction.CREATE;
                const url = new URL(base, window.location.origin); // Resolves relative path

                url.searchParams.set("DataInizio", info.dateStr);
                url.searchParams.set("Calendario", params.name);

                if (createParams) {
                    url.searchParams.set("Params", JSON.stringify(createParams));
                }

                switch (windowType) {
                    case InCoreCalendarWindowType.Modal:
                        InCoreModal(
                            controller,
                            InCoreModalAction.CREATE,
                            new InCoreModalLayout().ButtonsBelow(),
                            undefined,
                            refreshCalendars,
                            undefined,
                            ["DataInizio",
                            info.dateStr,
                            "Calendario",
                            params.name,
                            "Params",
                            JSON.stringify(createParams)]
                        );
                        break;

                    case InCoreCalendarWindowType.NewTab:
                        window.open(url.toString(), "_blank");
                        break;

                    case InCoreCalendarWindowType.SameWindow:
                        window.open(url.toString(), "_self");
                        break;
                }
            }
        },
        eventClick: (eventClickInfo) => {
            const id = eventClickInfo.event.id;
            if (readonly) {
                switch (windowType) {
                    case InCoreCalendarWindowType.Modal:
                        InCoreModal(
                            controller,
                            InCoreModalAction.DETAILS,
                            new InCoreModalLayout().ButtonsBelow(),
                            id,
                            refreshCalendars,
                            undefined,
                            []
                        );
                        break;

                    case InCoreCalendarWindowType.NewTab:
                        window.open(controller + "/" + InCoreModalAction.DETAILS + "/" + id, "_blank");
                        break;

                    case InCoreCalendarWindowType.SameWindow:
                        window.open(controller + "/" + InCoreModalAction.DETAILS + "/" + id, "_self");
                        break;
                }
            } else if (!disabled && !readonly) {
                switch (windowType) {
                    case InCoreCalendarWindowType.Modal:
                        InCoreModal(
                            controller,
                            InCoreModalAction.EDIT,
                            new InCoreModalLayout().ButtonsBelow(),
                            id,
                            refreshCalendars,
                            undefined,
                            ["Params",
                            JSON.stringify(editParams)]
                        );
                        break;

                    case InCoreCalendarWindowType.NewTab:
                        window.open(controller + "/" + InCoreModalAction.EDIT + "/" + id, "_blank");
                        break;

                    case InCoreCalendarWindowType.SameWindow:
                        window.open(controller + "/" + InCoreModalAction.EDIT + "/" + id, "_self");
                        break;
                }
            }
        },
        eventResize: (info) => {
            if (!disabled && !readonly) {
                const id = info.event.id;
                const deltaI = info.startDelta;
                const deltaF = info.endDelta;
                const rev = info.revert;
                editEvent("ResizeEvent", id, controller, deltaI, deltaF, rev);
            } else {
                info.revert();
                InCoreSwal2(InCoreSwal2Icon.Error, "Operazione non disponibile");
            }
        },
        eventDrop: (eventDropInfo) => {
            if (!disabled && !readonly) {
                const id = eventDropInfo.event.id;
                const delta = eventDropInfo.delta;
                const rev = eventDropInfo.revert;
                editEvent("MoveEvent", id, controller, delta, undefined, rev);
            } else {
                eventDropInfo.revert();
                InCoreSwal2(InCoreSwal2Icon.Error, "Operazione non disponibile");
            }
        },
        eventDidMount:  (eventMountInfo) => {
            const calendarCard = '<div class="calendarCard {0}" style="border-color:{1}; color:{2}; background-color:{3};">' +
                '<div >{4} - {5}{ICON}</div>' +
                '<div>{8}</div>' +
                '</div>';

            const icona = Array.isArray(eventMountInfo.event.extendedProps) ? eventMountInfo.event.extendedProps.join('') : "";

            console.log(eventMountInfo.event.extendedProps);

            const cCard = calendarCard
                .replaceAll('{ICON}', icona)
                .replaceAll('{0}', eventMountInfo.event.classNames.join(' ')) // Classi aggiuntive
                .replaceAll('{1}', eventMountInfo.event.borderColor) // Colore Bordo
                .replaceAll('{2}', eventMountInfo.event.textColor) // Colore Testo e Sfondo Icona
                .replaceAll('{3}', eventMountInfo.event.backgroundColor) // Colore di sfondo
                .replaceAll('{4}', eventMountInfo.event.start?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) ?? "00:00") // Ora inizio
                .replaceAll('{5}', eventMountInfo.event.end?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) ?? "00:00") // Ora fine
                .replaceAll('{8}', eventMountInfo.event.title); // Titolo

            eventMountInfo.el.innerHTML = cCard
        }
    });

    calendar.render();
    $(`#${id}`).data("calen", calendar);
    adjustCalendarHeight();

    return calendar;
}

/**
 * Refreshes all FullCalendar instances on the page.
 */
export function refreshCalendars(): void {
    $("[data-calendar]").each((_, element) => {
        $(element).data("calen").refetchEvents();
    });
    adjustCalendarHeight();
}

/**
 * Edits an event by sending a POST request.
 * @param {string} func - The server-side function to call.
 * @param {string} id - The event ID.
 * @param {string} controller - The controller name.
 * @param {any} deltaI - The start delta.
 * @param {any} deltaF - The end delta.
 * @param {Function} rev - The revert function.
 */
export function editEvent(
    func: string,
    id: string,
    controller: string,
    deltaI: any,
    deltaF: any,
    rev: Function
): void {
    const data = deltaF
        ? { id, deltaInizio: deltaI, deltaFine: deltaF }
        : { id, delta: deltaI };

    $.ajax({
        type: "POST",
        url: `/${controller}/${func}`,
        data: data,
        success: (result) => {
            if (result !== "success") {
                rev();
                InCoreSwal2(InCoreSwal2Icon.Error, result);
            }
        },
        error: (result) => {
            rev();
            InCoreSwal2(InCoreSwal2Icon.Error, result.responseText);
        },
    });
}

/**
 * Adjusts the height of the calendar to fix layout issues.
 */
export function adjustCalendarHeight(): void {
    const body = $(".fc-daygrid-body");
    if (body.hasClass("fc-daygrid-body-unbalanced")) {
        body.removeClass("fc-daygrid-body-unbalanced").addClass("fc-daygrid-body-balanced");
    }
}